/**
 * Template variable system for HTML-based report templates
 *
 * Variables use the syntax:
 * - Simple: {{variavel}}
 * - Groups: {{grupo[N].propriedade}} where N is the position (1-indexed)
 *
 * Group indexing:
 * - N refers to the position in the AVAILABLE grupos array
 * - The array is filtered (radar=true) and sorted by ordemQuestionario
 * - {{grupo[1]}} = first available group
 * - {{grupo[2]}} = second available group
 * - NOT based on ordemQuestionario value
 *
 * Examples:
 * - {{empresa_nome}} -> "Acme Corporation"
 * - {{grupo[1].titulo}} -> "Inovação" (first available group)
 * - {{grupo[2].media}} -> "75.5%" (second available group)
 *
 * They are replaced with actual data during PDF generation
 */

/**
 * Variable category for organization in the UI
 */
export enum VariableCategory {
  EMPRESA = "empresa",
  QUESTIONARIO = "questionario",
  RESULTADOS = "resultados",
  COMPONENTES = "componentes",
  GRUPOS = "grupos",
  IMAGENS = "imagens"
}

/**
 * Metadata for a template variable
 */
export interface TemplateVariable {
  /** Variable name (without curly braces) */
  name: string;
  /** Human-readable label */
  label: string;
  /** Description of what this variable represents */
  description: string;
  /** Example value */
  example: string;
  /** Category for organization */
  category: VariableCategory;
  /** Data type */
  type: "string" | "number" | "date" | "component";
}

/**
 * Metadata for grupo (group) sub-properties
 */
export interface GroupVariableProperty {
  /** Property name */
  property: string;
  /** Human-readable label */
  label: string;
  /** Description */
  description: string;
  /** Example value */
  example: string;
  /** Data type */
  type: "string" | "number" | "component";
}

/**
 * Fixed template variables (non-dynamic)
 */
export const TEMPLATE_VARIABLES: TemplateVariable[] = [
  // EMPRESA
  {
    name: "empresa_nome",
    label: "Nome da Empresa",
    description: "Razão social ou nome fantasia da empresa avaliada",
    example: "Acme Corporation",
    category: VariableCategory.EMPRESA,
    type: "string"
  },
  {
    name: "empresa_cnpj",
    label: "CNPJ da Empresa",
    description: "CNPJ formatado da empresa",
    example: "12.345.678/0001-90",
    category: VariableCategory.EMPRESA,
    type: "string"
  },
  {
    name: "empresa_setor",
    label: "Setor da Empresa",
    description: "Setor de atuação da empresa",
    example: "Tecnologia",
    category: VariableCategory.EMPRESA,
    type: "string"
  },

  // QUESTIONÁRIO/PROJETO
  {
    name: "questionario_nome",
    label: "Nome do Questionário",
    description: "Nome do questionário respondido",
    example: "Diagnóstico de Inovação 2024",
    category: VariableCategory.QUESTIONARIO,
    type: "string"
  },
  {
    name: "questionario_descricao",
    label: "Descrição do Questionário",
    description: "Descrição detalhada do questionário",
    example: "Avaliação completa do nível de maturidade em inovação",
    category: VariableCategory.QUESTIONARIO,
    type: "string"
  },
  {
    name: "projeto_nome",
    label: "Nome do Projeto",
    description: "Nome do projeto vinculado",
    example: "Projeto Transformação Digital 2024",
    category: VariableCategory.QUESTIONARIO,
    type: "string"
  },
  {
    name: "data_geracao",
    label: "Data de Geração",
    description: "Data em que o relatório foi gerado",
    example: "23/01/2026",
    category: VariableCategory.QUESTIONARIO,
    type: "date"
  },
  {
    name: "gerado_por",
    label: "Gerado Por",
    description: "Nome do usuário que gerou o relatório",
    example: "João Silva",
    category: VariableCategory.QUESTIONARIO,
    type: "string"
  },

  // RESULTADOS
  {
    name: "media_geral",
    label: "Média Geral",
    description: "Média geral de todos os grupos/dimensões",
    example: "75.5%",
    category: VariableCategory.RESULTADOS,
    type: "number"
  },
  {
    name: "total_perguntas",
    label: "Total de Perguntas",
    description: "Número total de perguntas respondidas",
    example: "45",
    category: VariableCategory.RESULTADOS,
    type: "number"
  },
  {
    name: "total_grupos",
    label: "Total de Grupos/Dimensões",
    description: "Número total de grupos/dimensões avaliados",
    example: "8",
    category: VariableCategory.RESULTADOS,
    type: "number"
  },
  {
    name: "dimensao_maior_nota",
    label: "Dimensão com Maior Nota",
    description: "Nome da dimensão/grupo com melhor desempenho",
    example: "Inovação",
    category: VariableCategory.RESULTADOS,
    type: "string"
  },
  {
    name: "dimensao_menor_nota",
    label: "Dimensão com Menor Nota",
    description: "Nome da dimensão/grupo com menor desempenho",
    example: "Gestão de Pessoas",
    category: VariableCategory.RESULTADOS,
    type: "string"
  },

  // COMPONENTES VISUAIS - RADAR
  // Tamanho padrão: 350px | Pequeno: 200px | Médio: 300px | Grande: 450px
  // Também aceita tamanho customizado: {{radar:250}}
  {
    name: "radar",
    label: "Radar - Empresa",
    description: "Gráfico radar com os resultados da empresa (tamanho padrão 350px)",
    example: "[Gráfico Radar Empresa]",
    category: VariableCategory.COMPONENTES,
    type: "component"
  },
  {
    name: "radar:pequeno",
    label: "Radar Empresa (Pequeno)",
    description: "Gráfico radar da empresa em tamanho pequeno (200px)",
    example: "[Radar Pequeno]",
    category: VariableCategory.COMPONENTES,
    type: "component"
  },
  {
    name: "radar:medio",
    label: "Radar Empresa (Médio)",
    description: "Gráfico radar da empresa em tamanho médio (300px)",
    example: "[Radar Médio]",
    category: VariableCategory.COMPONENTES,
    type: "component"
  },
  {
    name: "radar:grande",
    label: "Radar Empresa (Grande)",
    description: "Gráfico radar da empresa em tamanho grande (450px)",
    example: "[Radar Grande]",
    category: VariableCategory.COMPONENTES,
    type: "component"
  },
  {
    name: "radar_geral",
    label: "Radar - Média Geral",
    description: "Gráfico radar comparando empresa vs média geral (tamanho padrão 350px)",
    example: "[Gráfico Radar Média Geral]",
    category: VariableCategory.COMPONENTES,
    type: "component"
  },
  {
    name: "radar_geral:pequeno",
    label: "Radar Média Geral (Pequeno)",
    description: "Gráfico radar média geral em tamanho pequeno (200px)",
    example: "[Radar Geral Pequeno]",
    category: VariableCategory.COMPONENTES,
    type: "component"
  },
  {
    name: "radar_segmento",
    label: "Radar - Média do Segmento",
    description: "Gráfico radar comparando empresa vs média do segmento (tamanho padrão 350px)",
    example: "[Gráfico Radar Segmento]",
    category: VariableCategory.COMPONENTES,
    type: "component"
  },
  {
    name: "radar_segmento:pequeno",
    label: "Radar Segmento (Pequeno)",
    description: "Gráfico radar média do segmento em tamanho pequeno (200px)",
    example: "[Radar Segmento Pequeno]",
    category: VariableCategory.COMPONENTES,
    type: "component"
  },
  {
    name: "tabela_resultados",
    label: "Tabela de Resultados",
    description: "Tabela com resultados por dimensão (nome, média, classificação)",
    example: "[Tabela de Resultados]",
    category: VariableCategory.COMPONENTES,
    type: "component"
  },
  {
    name: "cabecalho",
    label: "Cabeçalho Padrão",
    description: "Cabeçalho com logo e informações da empresa",
    example: "[Cabeçalho]",
    category: VariableCategory.COMPONENTES,
    type: "component"
  },
  {
    name: "pagina",
    label: "Número de Página",
    description: "Exibe 'Pág N de X' no documento",
    example: "Pág 1 de 5",
    category: VariableCategory.COMPONENTES,
    type: "component"
  },
  {
    name: "quebra_pagina",
    label: "Quebra de Página",
    description: "Insere uma quebra de página no PDF",
    example: "[Nova Página]",
    category: VariableCategory.COMPONENTES,
    type: "component"
  }
];

/**
 * Properties available for each grupo/dimensão
 * Usage: {{grupo[N].property}} where N is the position (1-indexed) in available grupos
 *
 * Note: N refers to position in the filtered array (radar=true), not ordemQuestionario
 *
 * Examples:
 * - {{grupo[1].titulo}} -> Name of first available group
 * - {{grupo[1].media}} -> Average of first available group
 * - {{grupo[2].feedback}} -> Feedback of second available group
 */
export const GROUP_VARIABLE_PROPERTIES: GroupVariableProperty[] = [
  {
    property: "nome",
    label: "Nome do Grupo",
    description: "Nome do grupo/dimensão (alias de titulo)",
    example: "Inovação",
    type: "string"
  },
  {
    property: "titulo",
    label: "Título do Grupo",
    description: "Nome do grupo/dimensão",
    example: "Inovação",
    type: "string"
  },
  {
    property: "descricao",
    label: "Descrição do Grupo",
    description: "Descrição detalhada do grupo/dimensão",
    example: "Dimensão focada em inovação",
    type: "string"
  },
  {
    property: "media",
    label: "Média do Grupo",
    description: "Média percentual do grupo",
    example: "75.5%",
    type: "number"
  },
  {
    property: "feedback",
    label: "Feedback do Grupo",
    description:
      "Feedback apropriado baseado na pontuação do grupo (selecionado automaticamente da lista de feedbacks configurados)",
    example: "O grupo demonstra potencial de desenvolvimento...",
    type: "string"
  },
  {
    property: "classificacao",
    label: "Classificação",
    description: "Título da classificação baseada na pontuação (ex: ESSENCIAL, INCREMENTAL, PROTAGONISTA)",
    example: "INCREMENTAL",
    type: "string"
  },
  {
    property: "badge",
    label: "Badge de Classificação",
    description: "Badge estilizado com a classificação do grupo (HTML renderizado)",
    example: "[Badge INCREMENTAL]",
    type: "component"
  }
];

/**
 * Get all variable names for quick lookup
 */
export const VARIABLE_NAMES = TEMPLATE_VARIABLES.map((v) => v.name);

/**
 * Get variables by category
 * @param category
 */
export function getVariablesByCategory(category: VariableCategory): TemplateVariable[] {
  return TEMPLATE_VARIABLES.filter((v) => v.category === category);
}

/**
 * Get variable by name
 * @param name
 */
export function getVariable(name: string): TemplateVariable | undefined {
  return TEMPLATE_VARIABLES.find((v) => v.name === name);
}

/**
 * Check if a string contains template variables
 * @param template
 */
export function hasVariables(template: string): boolean {
  return /{{[^}]+}}/.test(template);
}

/**
 * Extract all variable names from a template string
 * @param template
 */
export function extractVariables(template: string): string[] {
  const matches = template.match(/{{([^}]+)}}/g);
  if (!matches) return [];

  return matches.map((match) => match.replaceAll(/{{|}}/g, "").trim());
}

/**
 * Validate if all variables in template are known
 * Returns array of unknown/invalid variable names
 *
 * For grupo variables: validates syntax {{grupo[N].property}} where N >= 1
 * @param template - Template string to validate
 * @param totalGrupos - Total number of grupos available (optional, for range validation)
 */
export function validateVariables(template: string, totalGrupos = 0): string[] {
  const variables = extractVariables(template);
  const unknown: string[] = [];

  for (const variable of variables) {
    // Check if it's a grupo variable pattern: grupo[N].property
    const grupoMatch = variable.match(/^grupo\[(\d+)]\.([_a-z]+)$/);

    if (grupoMatch) {
      const ordemString = grupoMatch[1];
      const property = grupoMatch[2];

      if (!ordemString || !property) {
        unknown.push(variable);
        continue;
      }

      const ordem = Number.parseInt(ordemString, 10);

      // Validate ordem is >= 1 (1-indexed)
      if (Number.isNaN(ordem) || ordem < 1) {
        unknown.push(variable);
        continue;
      }

      // Validate ordem is within range (if totalGrupos provided)
      if (totalGrupos > 0 && ordem > totalGrupos) {
        unknown.push(variable);
        continue;
      }

      // Check if property is valid
      if (!GROUP_VARIABLE_PROPERTIES.some((p) => p.property === property)) {
        unknown.push(variable);
        continue;
      }
    } else {
      // Check if it's a known fixed variable
      if (!VARIABLE_NAMES.includes(variable)) {
        unknown.push(variable);
      }
    }
  }

  return unknown;
}

// ============================================================================
// Image Variables - Dynamic variables based on template images
// ============================================================================

/**
 * Image variable info for display in UI
 */
export interface ImageVariable {
  /** Variable name (e.g., "imagem.capa") */
  name: string;
  /** Human-readable label */
  label: string;
  /** Description */
  description: string;
  /** The image URL */
  url: string;
}

/**
 * Generate image variables from template images
 * @param images - Array of template images (from TemplateDocument.imagens)
 * @returns Array of ImageVariable for UI display
 * @example
 * const images = [{ id: '1', nome: 'capa', url: '...' }];
 * const vars = generateImageVariables(images);
 * // => [{ name: 'imagem.capa', label: 'capa', url: '...' }]
 */
export function generateImageVariables(images: Array<{ nome: string; url: string }>): ImageVariable[] {
  return images.map((image) => ({
    name: `imagem.${image.nome}`,
    label: image.nome,
    description: `URL da imagem "${image.nome}" para uso como background`,
    url: image.url
  }));
}

/**
 * Get the full variable syntax for an image
 * @param imageName - The image name (e.g., "capa")
 * @returns The variable syntax (e.g., "{{imagem.capa}}")
 */
export function getImageVariableSyntax(imageName: string): string {
  return `{{imagem.${imageName}}}`;
}

/**
 * Check if a variable is an image variable
 * @param variable - Variable name without braces (e.g., "imagem.capa")
 */
export function isImageVariable(variable: string): boolean {
  return variable.startsWith("imagem.");
}

/**
 * Extract image name from an image variable
 * @param variable - Variable name (e.g., "imagem.capa")
 * @returns Image name (e.g., "capa") or null if not an image variable
 */
export function extractImageName(variable: string): string | null {
  if (!isImageVariable(variable)) return null;
  return variable.slice(7); // Remove "imagem." prefix
}

/**
 * Validate image variables in a template
 * @param template - Template HTML string
 * @param availableImages - Array of available image names
 * @returns Array of invalid image variable names
 */
export function validateImageVariables(template: string, availableImages: string[]): string[] {
  const variables = extractVariables(template);
  const invalidImages: string[] = [];

  for (const variable of variables) {
    if (isImageVariable(variable)) {
      const imageName = extractImageName(variable);
      if (imageName && !availableImages.includes(imageName)) {
        invalidImages.push(variable);
      }
    }
  }

  return invalidImages;
}
